import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Box,
  Typography,
  Stack,
} from "@mui/material";
import { Bus, Package, Gavel } from "lucide-react";
import { useTranslation } from "react-i18next";

const formatEur = (n) => `€${Number(n).toFixed(2)}`;

export function EcoOptionsModal({
  open,
  onClose,
  ecoOptions = [], // Array of { id, price, title, description }
  onSelectBus,
  onSelectRegular,
  onSelectBid,
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);

  // Set first option as default when modal opens
  useEffect(() => {
    if (open && ecoOptions.length > 0) {
      setSelected(ecoOptions[0].id);
    }
  }, [open, ecoOptions]);

  const handleClose = () => {
    setSelected(null);
    onClose();
  };

  const handleConfirm = () => {
    if (!selected) return;

    const selectedOption = ecoOptions.find(opt => opt.id === selected);

    if (selected === "bus") {
      onSelectBus?.(selectedOption);
    } else if (selected === "regular") {
      onSelectRegular?.(selectedOption);
    } else if (selected === "bid") {
      onSelectBid?.(selectedOption);
    }

    setSelected(null);
  };

  const ICONS = {
    bus: Bus,
    regular: Package,
    bid: Gavel,
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Typography component="span" variant="h6" fontWeight={700}>
          {t("delivery.selectEcoOption") || "Select Eco Delivery Option"}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={1.5}>
          {ecoOptions.map((opt) => {
            const isSelected = selected === opt.id;
            const Icon = ICONS[opt.id] || Package;

            return (
              <Card
                key={opt.id}
                onClick={() => setSelected(opt.id)}
                sx={{
                  cursor: "pointer",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isSelected ? "primary.main" : "divider",
                  boxShadow: isSelected ? 3 : 0,
                  transition: "all 0.15s ease",
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                    <Box sx={{ display: "flex", gap: 1.25, minWidth: 0 }}>
                      <Box
                        sx={{
                          mt: "2px",
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: isSelected ? "primary.50" : "action.hover",
                        }}
                      >
                        <Icon size={18} />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700 }}>
                          {opt.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                          {opt.description}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ textAlign: "right", minWidth: 80 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                          {t("delivery.startingFrom") || "From"}
                        </Typography>
                        <Typography sx={{ fontWeight: 900 }}>
                          {formatEur(opt.price ?? 0)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>
          {t("common.cancel") || "Cancel"}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selected}
        >
          {t("common.confirm") || "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}